import React, { createRef, useState, useMemo, useEffect, ReactElement } from 'react'
import tw, { styled } from 'twin.macro'
import {sortBy} from 'lodash'
import {
    useQuery,
    useQueryClient,
    QueryClient,
    QueryClientProvider,
  } from 'react-query'
import { useForm } from 'react-hook-form'
import { ChevronDown, X, Search } from 'react-feather'
import { CircleFlag } from 'react-circle-flags'

//import { Card } from '@nova-energy/ui-kit'

import Spinner from '@ui-kit/atoms/spinner'
import TextField from '@ui-kit/atoms/form/textfield'

import './styles.css'

type RoamingMapOptionsConfig = {
    heading: string,
    body: string,
    rates: Array<object>,
    image: ReactElement | null
}

type Country = {
    label: string,
    code: string,
    price: string
}

const queryClient = new QueryClient()

const RoamingMap: React.FC<RoamingMapOptionsConfig> = ({ heading, rates, image, ...props }) => {

    return (
        <Style className='promotion roaming-map'>
            <div className="promotion__container">
                <figure className="promotion__map">
                    <QueryClientProvider client={queryClient}>
                        <Map heading={heading} rates={rates} image={image} />
                    </QueryClientProvider>
                </figure>
            </div>
        </Style>
    )
}
export default RoamingMap


const Map = ({ heading, rates, image }) => {
    const { register, watch, handleSubmit } = useForm();
    const { data, isLoading, isError } = useQuery('roaming-data', async () => {
        return await fetch(`/roaming-data.json`).then(async res => await res.json());
    })

    const [ countrySearch, setCountrySearch ] = useState('');
    const [ selected, setSelected ] = useState(null);

    const destinationField = watch('destination', '');

    if(isLoading) {
        return <Spinner label={'Loading'} />
    }

    let countries = {}
    Object.keys(data).forEach(price => data[price].forEach(country => {
        //countries.push(country.name.toLowerCase());
        const id = country.name.toLowerCase()
        countries[id] = {
            label: country.name,
            code: country.code.toLowerCase(),
            price
        }
    }))

    const onSubmit = ({ destination }) => {
        setCountrySearch(destination.toLowerCase());
    }

    let compare = destinationField.toLowerCase();
    let suggestions: Array<string> = destinationField.length > 2 ? Object.keys(countries).filter(country => country.includes(compare)) : []

    const selectedCountry = selected ? countries[selected] : null;

    const plan = selectedCountry ? data[selectedCountry?.price] : null;

    let price = null;
    if(selectedCountry) {
        price = rates.find(({ code }) => code === selectedCountry.price);
    }

    return (
        <RoamingMapStyle className="roaming-map__inner">
            {heading && <h2>{heading}</h2>}
            <div className="roaming-map__filters">
                <form onSubmit={handleSubmit(onSubmit)}>
                <TextField
                    type="text"
                    id="destination"
                    name="destination"
                    reference={register}
                    inputMode={'search'}
                    //onChange={(e) => setCountrySearch(e.target.value)}
                    title="Tell us where you’re planning to go to!"
                    />
                    <button type="submit"><span tw="sr-only">Search</span><Search /></button>
                </form>
                <dl>
                    <dt>Popular destinations</dt>
                    <dd>
                        <ul>
                            <li><button disabled={selected === 'australia'} type="button" onClick={() => setSelected('australia')}>Australia</button></li>
                            <li><button disabled={selected === 'fiji'} type="button" onClick={() => setSelected('fiji')}>Fiji</button></li>
                            <li><button disabled={selected === 'cook islands'} type="button" onClick={() => setSelected('cook islands')}>Cook Islands</button></li>
                            <li><button disabled={selected === 'usa'} type="button" onClick={() => setSelected('usa')}>United States</button></li>
                            <li><button disabled={selected === 'united kingdom'} type="button" onClick={() => setSelected('united kingdom')}>United Kingdom</button></li>
                            <li><button disabled={selected === 'india'} type="button" onClick={() => setSelected('india')}>India</button></li>
                            <li><button disabled={selected === 'indonesia'} type="button" onClick={() => setSelected('indonesia')}>Indonesia</button></li>
                        </ul>
                    </dd>
                </dl>
                {suggestions.length > 0 && (
                    <ul className="roaming-map__search-suggestions">
                        {suggestions.map(suggestion => {
                            const country = countries[suggestion];

                        return (<li key={suggestion}>
                            <button type="button" onClick={() => {
                                setSelected(suggestion)
                                setCountrySearch('')
                            }}>
                                <CircleFlag countryCode={country.code} width={35} height={35} /> {country.label}</button>
                        </li>)
                        })}
                    </ul>
                )}
            </div>

            {selectedCountry && price && (
                <output className="roaming-map__selected">
                    <h2>
                        <CircleFlag countryCode={selectedCountry.code} width={45} height={45} />{selectedCountry.label}

                        {price.cost && <span className="cost">${price.cost}{price.cost_suffix && <small> {price.cost_suffix}</small>}</span>}

                    </h2>
                    {price.description && <div tw="leading-relaxed text-lg space-y-3" dangerouslySetInnerHTML={{__html: price.description.html }} />}

                    <button type="button" onClick={() => setSelected(null)}>
                        <X />
                        <span tw="sr-only">Close</span>
                    </button>
                </output>
            )}

            <aside tw="mt-10">
                {rates.filter(rate => rate.r.parent.length === 0).map(rate => {
                    const subcodes = rates.filter(r => r.r.parent.find(o => o.tid === rate.tid)).map(({ code }) => code);

                    return (
                        <details className="roaming-map__disclosure" key={rate.tid}>
                            <summary><strong>{rate.name}</strong><ChevronDown /></summary>
                            {rate.description && <div dangerouslySetInnerHTML={{__html: rate.description.html}} />}
                            {rate.code && (
                                <CountryList codes={[ rate.code, ...subcodes]} data={data} />
                            )}
                            {rate.smallprint && <div className="smallprint" dangerouslySetInnerHTML={{__html: rate.smallprint.html }} />}
                        </details>
                    )
                })}
            </aside>
            {image}
        </RoamingMapStyle>
    )
}

type CountryListType = {
    codes: Array<string>,
    data: any
}

const CountryList = ({ codes, data }: CountryListType) => {
    let countries: Array<any> = [];
    codes.forEach(code => {
        const set = data[code];
        if(!set) {
            return;
        }
        set.forEach((country:any) => countries = [...countries, { ...country, code }])
    })
    return (
        <ul className="roaming-map__countries">
            {sortBy(countries, 'name').map(country => (
                <li data-code={country.code} className={country.id === 'AU' ? '-order-1' : ''} key={country.id}>{country.name}</li>
            ))}
        </ul>
    )
}


const Style = styled.section`

    > .promotion__container {
        ${tw `maxWidth[1200px] lg:min-h-[720px]`}
    }

    .gatsby-image-wrapper {
        ${tw `hidden xl:block mg:transform[translateX(20%)]`}
    }

`

const RoamingMapStyle = styled.div`

    .roaming-map__selected {
        ${tw `p-7 rounded mt-10 block relative`}
        border: 1px solid rgba(0,0,0,.5);

        box-shadow: 0 0 1em rgb(0 0 0 / 15%);

        > button {
            ${tw `absolute top-3 right-3 md:top-5 md:right-5`}
        }

        h2 {
            ${tw `flex flex-wrap lg:flex-nowrap gap-y-2 items-center font-semibold text-2xl gap-x-4 mb-5`}

            img {
                ${tw `mb-0`}
            }

            .cost {
                ${tw `text-2xl lg:text-3xl text-[var(--nova-pink)] text-left leading-tight`}
            }
        }

        ul {
            ${tw `list-disc pl-6`}
        }
    }

    aside, .roaming-map__filters, .roaming-map__selected {
        ${tw `mx-auto max-w-[720px] z-20 relative`}
    }

    .roaming-map__filters form {
        > button {
            ${tw `w-12 h-12 flex items-center justify-center hover:text-pink focus-visible:text-pink`}

            svg {
                ${tw `w-2/3 h-2/3`}
            }
        }
    }

    .roaming-map__filters dl {
        ${tw `flex mb-0 mt-4 gap-x-2 items-center flex-wrap`}

        dt {
            ${tw `uppercase font-light text-gray-400 text-sm w-full md:w-auto`}
        }

        dd {
            ${tw `mb-0 text-sm`}

            ul {
                ${tw `flex flex-wrap gap-1 items-center`}

                li {
                    ${tw `mb-0`}

                    &:not(:last-child)::after {
                        content: ",";
                    }
                }
            }
        }
    }

    .roaming-map__disclosure {
        ${tw `border-t border-silver pt-5`}

        summary {
            ${tw `list-none text-left text-lg font-medium flex items-center gap-x-2 cursor-pointer hover:text-pink focus-visible:text-orange outline-none justify-between pb-5`}
        }
        summary::-webkit-details-marker {
            display: none;
        }

        > div {
            ${tw `text-base space-y-2 pb-5`}

            small a {
                ${tw `underline underline-offset-2`}
            }
        }

        > .smallprint {
            ${tw `text-sm mt-2 space-y-1 text-gray-600`}

            a {
                ${tw `underline underline-offset-2`}
            }
        }

        .roaming-map__countries {
            ${tw `text-sm flex flex-wrap gap-x-[.25em] gap-y-[.3em] pb-5`}

            > li:not(:last-child)::after {
                content: ",";
            }

            > li:is([data-code="DTPM"])::after {
                content: "*,";
            }

            /*> li:is([data-code="USAPM"])::after {
                content: "^,"
            }*/

            > .\-order-1 {
                order: -1;
            }
        }

        *:not(summary) + .roaming-map__countries {
            ${tw `mt-5`}
        }
    }
`